import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Grid2, Checkbox, IconButton,  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Menu, MenuItem, Tooltip } from '@mui/material';
import { ReactComponent as EditIcon } from '../../../assets/SVG/edit icon.svg';
import { ReactComponent as ViewIcon } from '../../../assets/SVG/eye.svg';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from '../../Inventory/Common/Inventory.module.css';
import ApplyTaxModal from './ApplyTaxModal';
import AssignTariffModal from './AssignTariffModal';
import { useAppQuery } from '../../../services/appQuery';
import { getPackageListURL, PackageDetails, PackageListResponse } from './PackageDefinition.helper';
import { getRequest, patchRequest } from '../../../services/httpService';
import StatusConfirmDialog, { StatusDialogVariant } from '../../../components/confirmationModal/StatusConfirmDialog';
import { useAlert } from '../../../components/alertProvider';
import { toggleStatusURL, freezePackageURL } from './PackageConfigHelper';
import CustomPagination from '../../../components/common/CustomPagination';
import { convertDateToDDMMYYYY } from '../../../utils/commonUtils';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import useCurrency from '../../../components/CurrencyProvider';
import { useTranslation } from 'react-i18next';


const fmtAmount = (n: number) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });



const PackageList: React.FC = () => {
  const navigate = useNavigate();
  const { currencySymbol } = useCurrency();
  const { t: tBilling } = useTranslation('billing_config');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<PackageDetails | null>(null);
  const [openTax, setOpenTax] = useState(false);
  const [openTariff, setOpenTariff] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusDialogVariant, setStatusDialogVariant] = useState<StatusDialogVariant>('activate');
  const [selectedRowId, setSelectedRowId] = useState<string | number | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<string | number, { status?: boolean; freeze?: boolean }>>({});
  const { addAlert } = useAlert();
  const [pageSize, setPageSize] = useState(10);
  
  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => setPage(newPage);
  const location = useLocation();
  
  
  const { data: packageList, queryClient, refetch: refetchPackageList } = useAppQuery<PackageListResponse>(
    ['packageList', page, pageSize, q],
    () => getRequest<PackageListResponse>(
      `${getPackageListURL()}?limit=${pageSize}&offset=${page * pageSize}${q.trim() ? `&package_name=${encodeURIComponent(q.trim())}` : ''}`
    ),
    { 
      staleTime: 0,
      gcTime: 0,
      debounceMs: 300,
    }

  );

  // Refetch when navigated back with refetch flag from details page
  useEffect(() => {
    const state = (location as unknown as { state?: { refetch?: boolean; ts?: number } }).state;
    if (state?.refetch) {
      refetchPackageList();
    }
  }, [location, refetchPackageList]);

  const updatePackageStatus = async (_id: string | number, _variant: StatusDialogVariant) => {
    const idNumber = Number(_id);
    if (!Number.isFinite(idNumber)) {
      addAlert(tBilling('package_config.messages.invalid_package_id', { defaultValue: 'Invalid package id for status update.' }), 'error');
      return false;
    }

    let url: string;
    let body: unknown;

    if (_variant === 'freeze') {
      url = freezePackageURL(idNumber);
      body = {freeze: true};
    } else {
      url = toggleStatusURL(idNumber);
      body = { status: _variant === 'activate' };
    }

    try {
      let response: unknown;
      if (queryClient) {
        response = await queryClient.fetchQuery({
          queryKey: ['PackageStatusToggle', idNumber, _variant],
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          queryFn: () => patchRequest(url, body as any),
          staleTime: 0,
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        response = await patchRequest(url, body as any);
      }

      const backendError = (response as { error?: string } | undefined)?.error;
      if (backendError) {
        addAlert(backendError, 'error');
        return false;
      }

      if (_variant === 'freeze') {
        addAlert(tBilling('package_config.messages.package_frozen_success', { defaultValue: 'Package frozen successfully.' }), 'success');
      } else {
        addAlert(
          tBilling('package_config.messages.package_status_updated', {
            defaultValue: 'Package {{status}} successfully.',
            status: _variant === 'activate'
              ? tBilling('package_config.status.activated', { defaultValue: 'activated' })
              : tBilling('package_config.status.inactivated', { defaultValue: 'inactivated' })
          }),
          'success'
        );
      }
      return true;
    } catch (error) {
      const actionLabel =
        _variant === 'activate'
          ? 'activate'
          : _variant === 'inactivate'
            ? 'inactivate'
            : 'freeze';
      
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'An error occurred';
      
      addAlert(
        tBilling('package_config.messages.package_status_failed', {
          defaultValue: 'Failed to {{action}} package: {{error}}',
          action: actionLabel,
          error: errorMessage,
        }),
        'error'
      );
      return false;
    }
  };

  const handleStatusConfirm = async () => {
    if (selectedRowId == null) { setStatusDialogOpen(false); return; }
    try {
      const ok = await updatePackageStatus(selectedRowId, statusDialogVariant);

      // Only change frontend for activate/inactivate when API succeeds
      if (!ok) return;

      setStatusOverrides(prev => {
        const current = prev[selectedRowId] || {};
        if (statusDialogVariant === 'activate') {
          return { ...prev, [selectedRowId]: { ...current, status: true } };
        }
        if (statusDialogVariant === 'inactivate') {
          return { ...prev, [selectedRowId]: { ...current, status: false } };
        }
        if (statusDialogVariant === 'freeze') {
        // still only frontend change, no API
          return { ...prev, [selectedRowId]: { ...current, freeze: true } };
        }
        return prev;
      });
    } finally {
      setStatusDialogOpen(false);
    }
  };

  return (
    <Box sx={{ p: 3 }} data-testid="package-list-container">
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{tBilling('package_config.package_list', { defaultValue: 'Package List' })} ({packageList?.meta.total_count})</Typography>

      <Grid2 container spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
          <TextField
            data-testid="pkglist-search-input"
            placeholder={tBilling('package_config.placeholders.search_package_name', { defaultValue: 'Search package name' })}
            value={q}
            onChange={e => { setQ(e.target.value); setPage(0); }}
            fullWidth
            size="small"
        
          />
        </Grid2>
        <Grid2 size="auto">
          <Button data-testid="pkglist-add-button" variant="contained" color="primary" sx={{ borderRadius: 2, fontWeight: 600, backgroundColor: '#444', '&:hover': { backgroundColor: '#222' }}}
            onClick={() => navigate('/ai/configuration/package-configuration/details')}
            startIcon={<AddCircleOutlineOutlinedIcon />}
          >
            {tBilling('package_config.add_new_package', { defaultValue: 'Add New Package' })}
          </Button>
        </Grid2>
      </Grid2>

      <TableContainer component={Paper} sx={{ borderRadius: 2, elevation: 0, boxShadow: 'none' }} data-testid="package-list-table-container">
        <Table sx={{elevation: 0}} data-testid="package-list-table">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#fafafa' }}>
              <TableCell sx={{ width: 120 }}>{tBilling('package_config.headers.code', { defaultValue: 'Code' })}</TableCell>
              <TableCell sx={{ width: 250 }}>{tBilling('package_config.headers.package_name', { defaultValue: 'Package Name' })}</TableCell>
              <TableCell sx={{ width: 150 }}>{tBilling('package_config.headers.effective_date', { defaultValue: 'Effective Date' })}</TableCell>
              <TableCell sx={{ width: 200 }}>{tBilling('package_config.headers.expiry_date', { defaultValue: 'Expiry Date' })}</TableCell>
              <TableCell sx={{ width: 180 }}>{`${tBilling('package_config.headers.package_amount', { defaultValue: 'Package Amount' })} (${currencySymbol})`}</TableCell>
              <TableCell sx={{ width: 100 }}>{tBilling('package_config.headers.status', { defaultValue: 'Status' })}</TableCell>
              <TableCell sx={{ width: 150 }}>{tBilling('package_config.headers.package_freeze', { defaultValue: 'Package Freeze' })}</TableCell>
              <TableCell sx={{ width: 80 }}>{tBilling('package_config.headers.action', { defaultValue: 'Action' })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {packageList?.objects.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary">{tBilling('package_config.no_packages_found', { defaultValue: 'No packages found.' })}</Typography>
                </TableCell>
              </TableRow>
            ) : (
              packageList?.objects.map(row => {
                const isExpired = new Date(row.expiry_date).getTime() < Date.now();
                return (
                  <TableRow key={row.id} hover data-testid={`pkglist-row-${row.id}`}>
                    <TableCell sx={{ width: 120 }}>{row.code}</TableCell>
                    <TableCell sx={{ width: 250 }}>{row.package_name}</TableCell>
                    <TableCell sx={{ width: 150 }}>{convertDateToDDMMYYYY(row.effective_date)}</TableCell>
                    <TableCell sx={{ width: 200 }}>
                      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        <span>{convertDateToDDMMYYYY(row.expiry_date)}</span>
                        {isExpired && (
                          <Box 
                            component="span" 
                            sx={{ 
                              px: 1, 
                              py: 0.5, 
                              borderRadius: '12px', 
                              border: '1px solid #EF9A9A', 
                              backgroundColor: '#FFEBEE',
                              color: '#C62828', 
                              fontSize: '12px',
                              fontWeight: 500,
                              fontFamily: 'Montserrat, sans-serif'
                            }}
                          >
                            {tBilling('package_config.expired', { defaultValue: 'Expired' })}
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ width: 180 }}>{fmtAmount(Number(row.package_amount || 0))}</TableCell>
                    <TableCell sx={{ width: 100 }}>
                      <Checkbox
                        data-testid={`pkglist-row-${row.id}-status-checkbox`}
                        checked={
                          typeof statusOverrides[row.id]?.status === 'boolean'
                            ? Boolean(statusOverrides[row.id]?.status)
                            : Boolean(row.status)
                        }
                        onClick={() => {
                          setSelectedRowId(row.id);
                          setStatusDialogVariant((typeof statusOverrides[row.id]?.status === 'boolean' ? statusOverrides[row.id]?.status : row.status) ? 'inactivate' : 'activate');
                          setStatusDialogOpen(true);
                        }}
                        icon={<span className={styles.AutoCompleteDropDownIcon} />}
                        checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 150 }}>
                      <Checkbox
                        data-testid={`pkglist-row-${row.id}-freeze-checkbox`}
                        disabled
                        checked={
                          typeof statusOverrides[row.id]?.freeze === 'boolean'
                            ? Boolean(statusOverrides[row.id]?.freeze)
                            : Boolean(row.freeze)
                        }
                        onClick={() => {
                          setSelectedRowId(row.id);
                          setStatusDialogVariant('freeze');
                          setStatusDialogOpen(true);
                        }}
                        icon={<span className={styles.AutoCompleteDropDownIcon} />}
                        checkedIcon={<span className={styles.AutoCompleteDropDowncheckedIcon}>✔</span>}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 80 }}>
                      <Tooltip title={tBilling('package_config.edit_package', { defaultValue: 'Edit Package' })} arrow>
                        <IconButton 
                          data-testid={`pkglist-row-${row.id}-edit-button`}
                          size="small" 
                          onClick={() => navigate(`/ai/configuration/package-configuration/details?packageId=${row.id}`)}
                        >
                          {row.freeze ? <ViewIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => { setMenuAnchor(null); setMenuRow(null); }}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <MenuItem onClick={() => { 
          if (menuRow) {
            navigate('/ai/configuration/package-configuration/package-Definition', { state: { packageDetail: menuRow } });
          }
          setMenuAnchor(null); 
        }}>
          {tBilling('package_config.package_definition', { defaultValue: 'Package Definition' })}
        </MenuItem>
        <Tooltip
          title={menuRow?.freeze ? '' : tBilling('package_config.messages.freeze_to_apply_tax', { defaultValue: 'Freeze package to apply tax' })}
          placement="left"
          arrow
        >
          <span style={{ display: 'block', cursor: menuRow?.freeze ? 'pointer' : 'not-allowed' }}>
            <MenuItem
              disabled={!menuRow?.freeze}
              onClick={() => { setMenuAnchor(null); setOpenTax(true); }}
            >
              {tBilling('package_config.apply_tax', { defaultValue: 'Apply Tax' })}
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={menuRow?.freeze ? '' : tBilling('package_config.messages.freeze_to_assign_tariff', { defaultValue: 'Freeze package to assign tariff' })}
          placement="left"
          arrow
        >
          <span style={{ display: 'block', cursor: menuRow?.freeze ? 'pointer' : 'not-allowed' }}>
            <MenuItem
              disabled={!menuRow?.freeze}
              onClick={() => { setMenuAnchor(null); setOpenTariff(true); }}
            >
              {tBilling('package_config.assign_tariff', { defaultValue: 'Assign Tariff' })}
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      <ApplyTaxModal
        open={openTax}
        onClose={() => setOpenTax(false)}
        packageName={menuRow?.package_name}
        packageId={menuRow?.id}
      />
      <AssignTariffModal
        open={openTariff}
        onClose={() => setOpenTariff(false)}
        packageDetail={menuRow}
      />

      <StatusConfirmDialog
        open={statusDialogOpen}
        variant={statusDialogVariant}
        title={statusDialogVariant === 'freeze'
          ? tBilling('package_config.dialog.package_freeze', { defaultValue: 'Package Freeze' })
          : tBilling('package_config.dialog.package_status', { defaultValue: 'Package Status' })}
        description={statusDialogVariant === 'activate'
          ? tBilling('package_config.dialog.activate_confirm', { defaultValue: 'Would you like to activate your package?' })
          : statusDialogVariant === 'inactivate'
            ? tBilling('package_config.dialog.inactivate_confirm', { defaultValue: 'Would you like to inactivate your package?' })
            : tBilling('package_config.dialog.freeze_confirm', { defaultValue: 'Would you like to freeze your package?' })}
        onClose={() => setStatusDialogOpen(false)}
        onCancel={() => setStatusDialogOpen(false)}
        onConfirm={handleStatusConfirm}
      />

      <CustomPagination
        page={page}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[10,15,20,25]}
        totalCount={packageList?.meta.total_count || 0}
        onPageChange={handlePageChange}
      />
    </Box>
  );
};

export default PackageList;
